import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  RadioCardGroup,
  RadioCardItem,
  Spinner,
} from '@eureca/ui';
import { ArrowUpRightIcon } from '@eureca/ui/icons';

export type PublishVisibility = 'public' | 'private';

/** EU-450 — vaga incompleta que bloqueia a publicação, linkável em nova aba. */
export interface PublishDialogIncompleteJob {
  id: string;
  name: string | null;
}

export interface ProgramPublishDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  visibility: PublishVisibility;
  onVisibilityChange: (visibility: PublishVisibility) => void;
  onConfirm: () => void;
  /**
   * Surfaces the design's "Preencha completamente as vagas para publicar"
   * (Figma `Home / Publish / Behavior` — 15212:21203) when the program has
   * incomplete jobs. Fed by the publish-readiness pre-check on dialog open;
   * the Core PATCH gate (422 `incomplete_jobs`) stays the authority.
   */
  error?: string;
  /**
   * EU-450 — the jobs blocking publication. Each renders as a link that opens
   * the job edit hub (`/vagas/:id/editar`) in a new tab so the consultor
   * can complete it without losing the publish context.
   */
  incompleteJobs?: PublishDialogIncompleteJob[];
  /** True while the readiness pre-check is in flight — blocks Publicar. */
  isCheckingReadiness?: boolean;
}

export function ProgramPublishDialog({
  open,
  onOpenChange,
  visibility,
  onVisibilityChange,
  onConfirm,
  error,
  incompleteJobs,
  isCheckingReadiness,
}: ProgramPublishDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="gap-0">
        <DialogHeader className="gap-1.5 px-6 pt-6">
          <DialogTitle>Publicar</DialogTitle>
          <DialogDescription>
            Tudo pronto para publicar essa oportunidade? Escolha o tipo de publicação:
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-3 px-6 pt-5 pb-8">
          <RadioCardGroup
            value={visibility}
            onValueChange={(value) => onVisibilityChange(value as PublishVisibility)}
            className="flex-nowrap gap-3"
          >
            <RadioCardItem value="public" className="group flex-1 py-4">
              Público
            </RadioCardItem>
            <RadioCardItem value="private" className="group flex-1 py-4">
              Privado
            </RadioCardItem>
          </RadioCardGroup>
          {isCheckingReadiness ? (
            <p
              className="text-muted-foreground flex items-center gap-2 text-sm leading-5"
              data-testid="program-publish-checking"
            >
              <Spinner className="size-4" />
              Verificando as vagas do programa…
            </p>
          ) : null}
          {error ? (
            <p role="alert" className="text-sm leading-5 text-destructive">
              {error}
            </p>
          ) : null}
          {incompleteJobs && incompleteJobs.length > 0 ? (
            <ul className="flex flex-col gap-1" data-testid="program-publish-incomplete-jobs">
              {incompleteJobs.map((job) => (
                <li key={job.id}>
                  <a
                    href={`/vagas/${job.id}/editar`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-destructive inline-flex items-center gap-1 text-sm leading-5 underline underline-offset-2 hover:opacity-80"
                    data-testid={`program-publish-incomplete-job-${job.id}`}
                  >
                    {job.name?.trim() ? job.name : 'Vaga sem nome'}
                    <ArrowUpRightIcon className="size-3.5" aria-hidden />
                  </a>
                </li>
              ))}
            </ul>
          ) : null}
        </div>
        <DialogFooter className="px-6 pb-6">
          <DialogClose asChild>
            <Button variant="outline">Voltar</Button>
          </DialogClose>
          <Button onClick={onConfirm} disabled={Boolean(error) || Boolean(isCheckingReadiness)}>
            Publicar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
